import { type ComponentProps, type ReactNode } from 'react';
import clsx from 'clsx';

import useSlider from '../../hooks/useSlider';

import styles from './Slider.module.css';

const ITEM_GAP = 16;
const MIN_ITEM_WIDTH = 320;

const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z"
      fill="currentColor"
    />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      d="M4 11H16.17L10.58 5.41L12 4L20 12L12 20L10.59 18.59L16.17 13H4V11Z"
      fill="currentColor"
    />
  </svg>
);

interface SliderProps extends ComponentProps<'div'> {
  items: ReactNode[];
  visibleItems?: number;
  minItemWidth?: number;
  gapOffset?: number;
  hideButtonsWhenNotUsed?: boolean;
}

export default function Slider({
  items,
  visibleItems = 3,
  minItemWidth = MIN_ITEM_WIDTH,
  gapOffset = 0,
  className,
  hideButtonsWhenNotUsed,
  ...props
}: SliderProps) {
  const { scrollerRef, next, prev, canScroll, isAtStart, isAtEnd, thumbWidthPct, thumbPositionPct } = useSlider({
    itemCount: items.length,
  });

  const safeVisibleItems = Math.max(1, Math.floor(visibleItems));
  const safeMinItemWidth = Math.max(0, minItemWidth);
  const columns = Math.min(safeVisibleItems, Math.max(1, items.length));
  const fitsWithoutScroll = items.length <= safeVisibleItems;
  const equalWidth = `calc((100% - ${(columns - (hideButtonsWhenNotUsed ? 0 : 1)) * ITEM_GAP + gapOffset}px) / ${columns})`;
  const slideBasis = fitsWithoutScroll ? equalWidth : `max(${safeMinItemWidth}px, ${equalWidth})`;

  return (
    <div
      {...props}
      className={clsx(styles.wrapper, className, hideButtonsWhenNotUsed && styles.hideUnusedButtons)}
      role="region"
    >
      <div
        className={clsx(
          styles.viewport,
          canScroll && isAtEnd && styles.shadowLeft,
          canScroll && !isAtEnd && styles.shadowRight,
        )}
      >
        <button
          type="button"
          className={clsx(styles.arrow, isAtStart && hideButtonsWhenNotUsed && styles.elementHidden)}
          onClick={prev}
          disabled={isAtStart}
          aria-label="Previous"
        >
          <ArrowLeftIcon className={styles.arrowIcon} />
        </button>

        <div
          ref={scrollerRef}
          className={clsx(styles.scroller, styles['scroll-fade-x'])}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={styles.slide}
              style={{ flexBasis: slideBasis }}
            >
              {item}
            </div>
          ))}
        </div>

        <button
          type="button"
          className={clsx(styles.arrow, isAtEnd && hideButtonsWhenNotUsed && styles.elementHidden)}
          onClick={next}
          disabled={isAtEnd}
          aria-label="Next"
        >
          <ArrowRightIcon className={styles.arrowIcon} />
        </button>
      </div>

      {canScroll && (
        <div
          className={styles.scrollbar}
          aria-hidden="true"
        >
          <div
            className={styles.scrollbarThumb}
            style={{ width: `${thumbWidthPct}%`, left: `${thumbPositionPct}%` }}
          />
        </div>
      )}
    </div>
  );
}
